/*
 * Offline / Sync control plane for the Firebase-only ERP.
 *
 * Firestore remains the source of truth. IndexedDB here stores only a durable
 * operation journal/status metadata; Firestore itself stores the actual queued
 * document writes in its persistent cache. This separation means unsynced
 * business data is not duplicated into an ad-hoc database while users still
 * get a visible, durable Sync Center.
 */
import { disableNetwork, enableNetwork, waitForPendingWrites } from 'firebase/firestore';
import { db } from '../config/firebase';

const DB_NAME = 'coldstorage-offline-control-v1';
const DB_VERSION = 1;
const STORE_MUTATIONS = 'mutations';
const STORE_META = 'meta';
const FORCE_KEY = 'coldstorage.forceOffline';
const LAST_SYNC_KEY = 'coldstorage.lastSuccessfulSyncAt';
const EVENT = 'coldstorage:offline-sync-status';

const nowIso = () => new Date().toISOString();
const randomId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch (_) {}
  return `op_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const hasWindow = () => typeof window !== 'undefined';
const browserOnline = () => !hasWindow() || navigator.onLine !== false;
const readForcedOffline = () => {
  if (!hasWindow()) return false;
  try { return window.localStorage.getItem(FORCE_KEY) === 'true'; } catch (_) { return false; }
};

let memoryFallback = new Map();
let pendingCountCache = 0;
let failedCountCache = 0;
let syncing = false;
let lastError = '';

const openDb = () => new Promise((resolve, reject) => {
  if (!hasWindow() || !window.indexedDB) {
    resolve(null);
    return;
  }
  const request = window.indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(STORE_MUTATIONS)) {
      const store = database.createObjectStore(STORE_MUTATIONS, { keyPath: 'id' });
      store.createIndex('status', 'status', { unique: false });
      store.createIndex('createdAt', 'createdAt', { unique: false });
      store.createIndex('tenantId', 'tenantId', { unique: false });
    }
    if (!database.objectStoreNames.contains(STORE_META)) {
      database.createObjectStore(STORE_META, { keyPath: 'key' });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const withStore = async (storeName, mode, fn) => {
  const database = await openDb().catch(() => null);
  if (!database) return fn(null);
  return new Promise((resolve, reject) => {
    const tx = database.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    let result;
    try { result = fn(store); } catch (error) { reject(error); return; }
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted'));
  }).finally(() => database.close());
};

const emit = () => {
  if (!hasWindow()) return;
  window.dispatchEvent(new CustomEvent(EVENT, { detail: snapshot() }));
};

const snapshot = () => ({
  browserOnline: browserOnline(),
  forcedOffline: readForcedOffline(),
  effectiveOnline: browserOnline() && !readForcedOffline(),
  pendingCount: pendingCountCache,
  failedCount: failedCountCache,
  syncing,
  lastError,
  lastSyncedAt: hasWindow() ? (window.localStorage.getItem(LAST_SYNC_KEY) || '') : '',
});

const refreshCounts = async () => {
  const rows = await offlineSyncService.listMutations().catch(() => []);
  pendingCountCache = rows.filter((row) => row.status === 'pending' || row.status === 'syncing').length;
  failedCountCache = rows.filter((row) => row.status === 'failed').length;
  emit();
  return snapshot();
};

const putMutation = async (row) => {
  if (!hasWindow() || !window.indexedDB) {
    memoryFallback.set(row.id, row);
    return row;
  }
  await withStore(STORE_MUTATIONS, 'readwrite', (store) => store.put(row));
  return row;
};

const deleteMutation = async (id) => {
  if (!hasWindow() || !window.indexedDB) {
    memoryFallback.delete(id);
    return;
  }
  await withStore(STORE_MUTATIONS, 'readwrite', (store) => store.delete(id));
};

const updateMutation = async (id, patch) => {
  const rows = await offlineSyncService.listMutations();
  const current = rows.find((row) => row.id === id);
  if (!current) return null;
  const next = { ...current, ...patch, updatedAt: nowIso() };
  await putMutation(next);
  await refreshCounts();
  return next;
};

const normalizeError = (error) => String(error?.message || error?.code || error || 'Unknown sync error').slice(0, 500);

const offlineSyncService = {
  EVENT,

  getSnapshot: snapshot,

  isOfflineLikely() {
    return !browserOnline() || readForcedOffline();
  },

  subscribe(listener) {
    if (!hasWindow()) return () => {};
    const handler = (event) => listener(event?.detail || snapshot());
    window.addEventListener(EVENT, handler);
    const onlineHandler = () => { emit(); if (!readForcedOffline()) this.syncNow({ silent: true }); };
    const offlineHandler = () => emit();
    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);
    listener(snapshot());
    return () => {
      window.removeEventListener(EVENT, handler);
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', offlineHandler);
    };
  },

  async init() {
    await refreshCounts();
    if (readForcedOffline()) {
      await disableNetwork(db).catch((error) => {
        lastError = normalizeError(error);
      });
    } else if (browserOnline()) {
      // Do not block startup on Firebase acknowledgement.
      enableNetwork(db).catch(() => {});
      this.syncNow({ silent: true });
    }
    emit();
    return snapshot();
  },

  async setForcedOffline(value) {
    const enabled = Boolean(value);
    if (hasWindow()) {
      try { window.localStorage.setItem(FORCE_KEY, enabled ? 'true' : 'false'); } catch (_) {}
    }
    lastError = '';
    if (enabled) {
      await disableNetwork(db);
    } else {
      await enableNetwork(db);
      if (browserOnline()) this.syncNow({ silent: true });
    }
    emit();
    return snapshot();
  },

  async recordMutation(meta = {}) {
    const row = {
      id: meta.id || randomId(),
      tenantId: String(meta.tenantId || ''),
      module: String(meta.module || meta.collection || 'Firestore'),
      action: String(meta.action || 'write'),
      entityId: String(meta.entityId || ''),
      path: String(meta.path || ''),
      summary: String(meta.summary || '').slice(0, 240),
      status: 'pending',
      createdAt: nowIso(),
      updatedAt: nowIso(),
      retryCount: Number(meta.retryCount || 0),
      error: '',
      deviceId: this.getDeviceId(),
    };
    await putMutation(row);
    await refreshCounts();
    return row.id;
  },

  async markSynced(id) {
    // Synced journal entries are removed; the cloud document remains the audit source.
    await deleteMutation(id).catch(() => {});
    if (hasWindow()) {
      try { window.localStorage.setItem(LAST_SYNC_KEY, nowIso()); } catch (_) {}
    }
    lastError = '';
    await refreshCounts();
  },

  async markFailed(id, error) {
    lastError = normalizeError(error);
    await updateMutation(id, { status: 'failed', error: lastError });
    emit();
  },

  trackRemotePromise(id, promise) {
    Promise.resolve(promise)
      .then(() => this.markSynced(id))
      .catch((error) => this.markFailed(id, error));
  },

  async listMutations() {
    if (!hasWindow() || !window.indexedDB) {
      return Array.from(memoryFallback.values()).sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
    }
    const database = await openDb().catch(() => null);
    if (!database) return Array.from(memoryFallback.values());
    return new Promise((resolve, reject) => {
      const tx = database.transaction(STORE_MUTATIONS, 'readonly');
      const request = tx.objectStore(STORE_MUTATIONS).getAll();
      request.onsuccess = () => resolve((request.result || []).sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))));
      request.onerror = () => reject(request.error);
      tx.oncomplete = () => database.close();
    });
  },

  async retryFailed() {
    // Firestore rejected writes (for example permission-denied) are not safely
    // replayable from this metadata-only journal. Do not pretend they were
    // re-queued. Keep them visible for operator/admin review while syncing any
    // genuine pending Firestore writes that may still exist.
    if (!this.isOfflineLikely()) await this.syncNow({ silent: true });
    await refreshCounts();
    return snapshot();
  },

  async syncNow({ silent = false } = {}) {
    if (this.isOfflineLikely()) {
      if (!silent) lastError = readForcedOffline() ? 'Manual offline mode is enabled.' : 'Internet connection is unavailable.';
      emit();
      return snapshot();
    }
    syncing = true;
    lastError = '';
    emit();
    try {
      await enableNetwork(db);
      await waitForPendingWrites(db);
      // Firestore has acknowledged every write issued before waitForPendingWrites.
      const rows = await this.listMutations();
      await Promise.all(rows.filter((row) => row.status !== 'failed').map((row) => deleteMutation(row.id)));
      if (hasWindow()) window.localStorage.setItem(LAST_SYNC_KEY, nowIso());
    } catch (error) {
      lastError = normalizeError(error);
      if (!silent) console.warn('Offline sync failed:', error);
    } finally {
      syncing = false;
      await refreshCounts();
      emit();
    }
    return snapshot();
  },

  getDeviceId() {
    if (!hasWindow()) return 'server';
    const key = 'coldstorage.deviceId';
    try {
      let value = window.localStorage.getItem(key);
      if (!value) {
        value = `WEB-${Math.random().toString(36).slice(2, 8).toUpperCase()}-${Date.now().toString(36).toUpperCase()}`;
        window.localStorage.setItem(key, value);
      }
      return value;
    } catch (_) {
      return 'WEB-UNKNOWN';
    }
  },
};

export default offlineSyncService;
